import { SyntheticEvent, useState, useRef } from 'react';
import {
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@mui/material';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { RootState } from 'src/store/reducers';
import { getLabelFallback } from '@utils/utils';
import ArrowDown from '@images/svg-icons/arrowDown.svg';
import ErrorIcon from '@images/svg-icons/errorIcon.svg';
import SuccessIcon from '@images/svg-icons/successIcon.svg';
import CloseIcon from '@images/svg-icons/closeIcon.svg';
import { useSelector } from 'react-redux';

interface Form {
    name: string;
    city: string;
    state: string;
}

type SearchDoctorByNameProps = {
    getDoctor;
};

yup.addMethod(yup.object, 'atLeastOneOf', function (list) {
    return this.test({
        name: 'atLeastOneOf',
        message: '${path} must have at least one of these keys: ${keys}',
        exclusive: true,
        params: { keys: list.join(', ') },
        test: value => value == null || list.some(f => !!value[f]),
    });
});

const schema = yup
    .object()
    .shape({
        name: yup.string(),
        city: yup.string(),
        state: yup.string(),
    })
    // @ts-ignore
    .atLeastOneOf(['name', 'city', 'state']);

const SearchDoctorByName = (props: SearchDoctorByNameProps) => {
    const config = useSelector((state: RootState) => state.common.config);

    const defaultFormValues: Form = {
        name: '',
        city: '',
        state: '',
    };

    const initialFormStatus = {
        name: {
            focus: false,
            success: false,
        },
        city: {
            focus: false,
            success: false,
        },
        state: {
            focus: false,
            success: false,
        },
    };
    const [formStatus, setFormStatus] = useState(initialFormStatus);

    const ref = useRef(null);
    const [val, setVal] = useState(null);
    const [showItems, setShowItems] = useState(false);
    const [fromDefault, setFromDefault] = useState(false);
    const [searchByPhone, setSearchByPhone] = useState(true);

    const getItems = () => {
        return config.stateList.map((item, index) => {
            return (
                <li
                    key={index}
                    className={`custom-select-presentation-items-list-item ${
                        val === item ? 'selected' : ''
                    }`}
                    onClick={() => handleClick(item)}
                    onKeyDown={e => console.log(e.key)}
                >
                    <span className="custom-select-presentation-items-list-item-label">
                        {item.label}
                    </span>
                </li>
            );
        });
    };

    const handleClick = item => {
        if (!item.disabled) {
            setVal(item.label);
            setFromDefault(false);
        }
    };

    const methods = useForm<Form>({
        resolver: yupResolver(schema),
        mode: 'onBlur',
        defaultValues: defaultFormValues,
    });

    const {
        register,
        control,
        handleSubmit,
        setValue,
        clearErrors,
        setError,
        watch,
        formState: { errors, isSubmitting },
    } = methods;

    const watchName = watch('name');
    const watchCity = watch('city');
    const watchState = watch('state');

    const handleInputEvent = (e: SyntheticEvent) => {
        e.persist();
        const event = e.target as HTMLInputElement;

        if (e.type === 'focus') {
            clearErrors(event.name as keyof Form);
            setFormStatus({
                ...formStatus,
                [event.name]: { success: event.value.replace(/\D/g, '').length >= 10, focus: true },
            });
        }

        if (e.type === 'blur') {
            setFormStatus({
                ...formStatus,
                [event.name]: {
                    success: event.value.replace(/\D/g, '').length >= 10,
                    focus: false,
                },
            });
            setValue(event.name as keyof Form, event.value.trim());
            if (!event.value.trim()) {
                setError(event.name as keyof Form, {
                    type: 'required',
                    message: getLabelFallback(
                        'MYPROFILE_MY_PRESCRIPTIONS_GENERIC_FIELD_CANNOT_BE_EMPTY'
                    ),
                });
            }
        }
    };

    const submit = () => {
        try {
            schema.validateSync({
                name: watchName,
                city: watchCity,
                state: watchState,
            });
            props.getDoctor(null, watchName, watchCity, watchState);
        } catch {
            console.error("can't submit");
        }
    };

    return (
        <div className="search-doctor-modal_by-name">
            <div className="form-group">
                <TextField
                    {...register('name')}
                    className={`${
                        errors?.name?.message
                            ? 'form-field-error'
                            : formStatus.name.success
                            ? 'form-field-success'
                            : ''
                    }`}
                    onChange={handleInputEvent}
                    onFocus={handleInputEvent}
                    onBlur={handleInputEvent}
                    name="name"
                    variant="outlined"
                    label={'Clinic or Doctor name'}
                    InputProps={{
                        endAdornment: formStatus.name.focus ? (
                            <InputAdornment position="end">
                                <CloseIcon
                                    className="cursor-pointer"
                                    onMouseDown={() => setValue('name', '')}
                                />
                            </InputAdornment>
                        ) : errors?.name?.message ? (
                            <InputAdornment position="end">
                                <ErrorIcon />
                            </InputAdornment>
                        ) : formStatus.name.success ? (
                            <InputAdornment position="end">
                                <SuccessIcon />
                            </InputAdornment>
                        ) : null,
                    }}
                />

                <TextField
                    {...register('city')}
                    className={`${
                        errors?.city?.message
                            ? 'form-field-error'
                            : formStatus.city.success
                            ? 'form-field-success'
                            : ''
                    }`}
                    onChange={handleInputEvent}
                    onFocus={handleInputEvent}
                    onBlur={handleInputEvent}
                    name="city"
                    variant="outlined"
                    label={'City'}
                    InputProps={{
                        endAdornment: formStatus.city.focus ? (
                            <InputAdornment position="end">
                                <CloseIcon
                                    className="cursor-pointer"
                                    onMouseDown={() => setValue('city', '')}
                                />
                            </InputAdornment>
                        ) : errors?.city?.message ? (
                            <InputAdornment position="end">
                                <ErrorIcon />
                            </InputAdornment>
                        ) : formStatus.city.success ? (
                            <InputAdornment position="end">
                                <SuccessIcon />
                            </InputAdornment>
                        ) : null,
                    }}
                />

                <button
                    ref={ref}
                    className={`custom-select ${showItems ? 'focused' : ''} ${
                        !!val && val.length > 0 && !showItems ? 'valid' : ''
                    }`}
                    onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowItems(showItems => !showItems);
                    }}
                >
                    {!!val && (
                        <label className={`custom-select-top-label ${showItems ? 'focused' : ''}`}>
                            State
                        </label>
                    )}
                    <div className="custom-select-content">
                        <label>{!!val ? val : 'State'}</label>
                    </div>
                    <div className="custom-select-icon">
                        <ArrowDown />
                    </div>
                    <div className="custom-select-icons">
                        <div className="custom-select-icons-icon">
                            {val !== null ? <SuccessIcon /> : ''}
                        </div>
                    </div>
                </button>
                {showItems && (
                    <div
                        className="custom-select-presentation"
                        onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowItems(showItems => !showItems);
                        }}
                    >
                        <div className="custom-select-presentation-items">
                            <ul className="custom-select-presentation-items-list">{getItems()}</ul>
                        </div>
                    </div>
                )}
                {/*
                <Controller
                    name="state"
                    control={control}
                    render={({ field: { value }, fieldState, field }) => (
                        <FormControl>
                            <InputLabel id="doctor-state">State</InputLabel>
                            <Select
                                className={`state ${
                                    fieldState?.error?.message
                                        ? 'form-field-error'
                                        : value
                                        ? 'form-field-success'
                                        : ''
                                }`}
                                name="state"
                                labelId="doctor-state"
                                variant="outlined"
                                IconComponent={props => <ArrowDown {...props} />}
                                label="State"
                                endAdornment={
                                    fieldState?.error?.message ? (
                                        <InputAdornment
                                            position="end"
                                            sx={{
                                                width: '25px',
                                                position: 'absolute',
                                                right: '45px',
                                            }}
                                        >
                                            <ErrorIcon />
                                        </InputAdornment>
                                    ) : value ? (
                                        <InputAdornment
                                            position="end"
                                            sx={{
                                                width: '25px',
                                                position: 'absolute',
                                                right: '45px',
                                            }}
                                        >
                                            <SuccessIcon />
                                        </InputAdornment>
                                    ) : null
                                }
                                {...field}
                            >
                                {config.stateList.map(option => (
                                    <MenuItem
                                        key={option.code}
                                        value={option.code}
                                        disabled={option.disabled}
                                    >
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                />*/}
            </div>
            <div className="search-doctor-modal_submit">
                <button
                    className="primary-button button-size-medium"
                    onClick={e => {
                        e.preventDefault();
                        submit();
                    }}
                >
                    Submit
                </button>
            </div>
        </div>
    );
};

export default SearchDoctorByName;
